/**
 * Paginação das listagens: uma página por vez, com "Carregar mais".
 *
 * ## Por que paginar
 *
 * Nenhuma listagem do app paginava: cada tela pedia a tabela inteira. Enquanto
 * o banco tinha dezenas de linhas isso não custava nada; numa cheia de verdade
 * a fila de pedidos, a busca de abrigados e a lista de doações crescem sem teto,
 * e quem está com a rede que sobrou paga o download inteiro para ler as três
 * primeiras linhas.
 *
 * ## Deslocamento (`range`), e não cursor
 *
 * O Supabase pagina por faixa de linhas (`.range(from, to)`), que é
 * deslocamento — e deslocamento tem um defeito conhecido: se uma linha nova
 * entra **no topo** enquanto a pessoa lê, a página seguinte repete uma linha da
 * anterior (ou pula uma).
 *
 * O cursor por `created_at` resolveria isso, mas exige desempate por `id`
 * quando duas linhas têm o mesmo carimbo — e elas têm: o seed grava vários
 * registros no mesmo instante, e um resgate em massa também. Sem o desempate, o
 * cursor **pula** linhas, que é pior que repetir uma. Escrever esse desempate em
 * PostgREST (`or(created_at.lt.X, and(created_at.eq.X,id.lt.Y))`) custa uma
 * complexidade que estas telas ainda não pediram.
 *
 * ⚠️ Então a escolha é consciente: **repetir uma linha na virada é aceitável;
 * pular não seria.** Se alguma lista passar a exigir exatidão na virada (um
 * histórico exportável, por exemplo), aí vale o cursor — e o lugar de escrever
 * isso é aqui.
 */

/** Linhas por página. Cabe numa tela de telefone com folga para rolar. */
export const PAGE_SIZE = 20;

/**
 * A faixa `[from, to]` que o Supabase espera para uma página, contando de zero.
 *
 * `to` é **inclusivo** no PostgREST — daí o `-1`. Errar isso traz uma linha a
 * mais por página e faz a última página parecer cheia quando não está.
 */
export function pageRange(pageIndex: number, pageSize = PAGE_SIZE): [number, number] {
  const from = pageIndex * pageSize;

  return [from, from + pageSize - 1];
}

/**
 * O índice da próxima página, ou `undefined` quando a lista acabou — pronto
 * para ir direto no `getNextPageParam` do react-query.
 *
 * A pergunta é sobre a **última página recebida**: se ela veio menor que o
 * tamanho pedido, não há mais nada atrás dela. Uma página cheia não garante que
 * exista a próxima, e é por isso que a última rodada sempre volta vazia — o
 * preço de não pedir uma contagem total a cada leitura.
 *
 * ⚠️ **Esta função não recebe um terceiro parâmetro, e isso é proposital.** O
 * react-query chama `getNextPageParam(lastPage, allPages, lastPageParam,
 * allPageParams)`. Numa versão anterior daqui havia um `pageSize` opcional em
 * terceiro lugar, e o `lastPageParam` caía nele: virava `0`, `1`, `2`…,
 * `lastPage.length < pageSize` nunca era verdade e **a lista nunca terminava** —
 * o botão "Carregar mais" seguia buscando páginas vazias para sempre. Dois
 * agentes esbarraram nisso no mesmo dia e cada um embrulhou a chamada para se
 * defender. Um helper que só funciona se todo mundo lembrar de embrulhar vai ser
 * passado direto uma hora; então o parâmetro saiu. **Não o traga de volta** — se
 * uma lista precisar de outro tamanho de página, o caminho é uma função própria,
 * não um argumento opcional aqui.
 */
export function nextPageIndex(
  lastPage: readonly unknown[],
  pages: readonly unknown[][],
): number | undefined {
  return lastPage.length < PAGE_SIZE ? undefined : pages.length;
}
